import { t } from "@lingui/macro";
import { parseUnits } from "ethers";
import { useState, useCallback, useEffect, useMemo } from "react";

import { isDevelopment } from "config/env";
import { AssetChainTransaction } from "lib/apis/bridge/index.dto";
import { useBridge } from "lib/bridge/bridge";
import { Asset } from "lib/bridge/types";
import { getPageTitle } from "lib/legacy";
import { ChainId } from "lib/node/types";
import { useInputDebounce } from "lib/useDebounce";

import SEO from "components/Common/SEO";

import { ConfirmScreen } from "./components/ConfirmScreen";
import { EnterAmountScreen } from "./components/EnterAmountScreen";
import { ErrorScreen } from "./components/ErrorScreen";
import { HomeScreen } from "./components/HomeScreen";
import { NoAssetScreen } from "./components/NoAssetScreen";
import { ScanCompletedScreen } from "./components/ScanCompletedScreen";
import { ScanningScreen } from "./components/ScanningScreen";
import { SharePNLScreen } from "./components/SharePNLScreen";
import { TransferCompletedScreen } from "./components/TransferCompletedScreen";

type Screen =
  | "home"
  | "scanning"
  | "waiting"
  | "scan-completed"
  | "no-asset"
  | "enter-amount"
  | "confirm"
  | "transferring"
  | "transfer-completed"
  | "share-pnl"
  | "error";

function getParsedBridgeAmountInWei(amount: string, tokenDecimal: bigint) {
  const normalizedAmount = amount.trim();

  if (!normalizedAmount) {
    return undefined;
  }

  try {
    return parseUnits(normalizedAmount, tokenDecimal);
  } catch (error) {
    return undefined;
  }
}

function getTokenImage(symbol: string): string | undefined {
  const key = symbol.toUpperCase();

  if (key.includes("USDC")) {
    return "/assets/usdc.svg";
  }

  if (key.includes("USDT")) {
    return "/assets/usdt.svg";
  }

  if (key.includes("WBTC")) {
    return "/assets/WBTC.png";
  }

  if (key.includes("WETH")) {
    return "/assets/WETH.png";
  }

  if (key.includes("WNT")) {
    return "/assets/WNT.png";
  }

  if (key.includes("XRWA") || key === "RWA") {
    return "/assets/XRWA.png";
  }

  if (key.includes("BTC")) {
    return "/assets/BTC.png";
  }

  return undefined;
}

function getChainImage(chainId: string): string | undefined {
  switch (chainId) {
    case "42421":
      return "/assets/assetChain.svg";
    case "42420":
      return "/assets/assetChain.svg";
    case "97":
      return "/binance.svg";
    case "56":
      return "/binance.svg";
    case "84532":
      return "/assets/Base.png";
    case "8453":
      return "/assets/Base.png";
    case "11155111":
      return "/assets/Ethereum.png";
    case "1":
      return "/assets/Ethereum.png";
    case "421614":
    case "42161":
      return "/arbitrum.svg";
    case "80001":
      return "/assets/polygon.svg";
    case "80002":
      return "/assets/polygon.svg";
    case "137":
      return "/assets/polygon.svg";
    case "200901":
      return "/assets/bitlayer.png";
    case "200810":
      return "/assets/bitlayer.png";
    default:
      return undefined;
  }
}

// const MOCK_ASSETS: Asset[] = [
//   { name: "USDC", balance: "353", iconVariant: "usdc" },
//   { name: "USDT", balance: "353,555", iconVariant: "usdt" },
//   { name: "USDC", balance: "35", iconVariant: "usdc-alt" },
//   { name: "USDC", balance: "35", iconVariant: "usdc-alt" },
// ];

const supportedAssets = isDevelopment()
  ? ["USDC", "BTC", "WNT", "XRWA", "RWA"]
  : ["USDC", "USDT", "BTC", "WETH", "WNT", "XRWA", "RWA", "WBTC"];

type TransferState = "approving" | "transferring" | "claiming" | "block-confirming";
export default function Bridge() {
  const [screen, setScreen] = useState<Screen>("home");
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedScanChainId, setSelectedScanChainId] = useState<ChainId>("42421");
  const [hasSelectedSourceChain, setHasSelectedSourceChain] = useState(false);
  const [bridgeAmount, setBridgeAmount] = useState(selectedAsset?.balance || "");
  const [userHasAllowance, setUserHasAllowance] = useState(false);
  const [transferState, setTransferState] = useState<TransferState>("approving");
  const [claimTx, setClaimTx] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<AssetChainTransaction | null>(null);
  const {
    getBalances,
    loadingBalances,
    balances,
    hasAllowance,
    decimals,
    tokens,
    approve,
    send,
    claimToken,
    confirmBlock,
  } = useBridge();

  const checkAllowance = useCallback(async () => {
    if (!selectedAsset?.chainId || !bridgeAmount) return;
    const numericBridgeAmount = Number(bridgeAmount);
    if (!Number.isFinite(numericBridgeAmount) || numericBridgeAmount <= 0) {
      setUserHasAllowance(false);
      return;
    }

    const allowance = await hasAllowance(selectedAsset.chainId as ChainId, numericBridgeAmount, selectedAsset.symbol);
    setUserHasAllowance(allowance);
  }, [selectedAsset, bridgeAmount, hasAllowance]);

  const debouncedCheckAllowance = useInputDebounce(checkAllowance, 500);

  useEffect(() => {
    debouncedCheckAllowance();
  }, [debouncedCheckAllowance]);

  const handleSelectSourceChain = useCallback((chainId: ChainId) => {
    setSelectedScanChainId(chainId);
    setHasSelectedSourceChain(true);
  }, []);

  const handleScanWallet = useCallback(() => {
    setScreen("scanning");
    getBalances([selectedScanChainId]);
  }, [getBalances, selectedScanChainId]);

  const handleScanCompleted = useCallback(() => {
    if (loadingBalances) {
      setScreen("scanning");
    } else {
      if (!balances) {
        setScreen("no-asset");
      } else if (Object.keys(balances).length === 0) {
        setScreen("no-asset");
      }
      const keys = Object.keys(balances);
      let hasBalance = false;
      keys.forEach((key) => {
        const chainBalances = balances[key as ChainId];
        if (chainBalances) {
          chainBalances.forEach((balance) => {
            if (balance.amountCanBridge > 0) {
              hasBalance = true;
              return;
            }
          });
        }
      });
      if (hasBalance) {
        setScreen("scan-completed");
      } else {
        setScreen("no-asset");
      }
    }
  }, [loadingBalances, balances]);

  // Only apply scan result (no-asset / scan-completed) when we're already on "scanning".
  // On initial render we stay on "home"; user must click a chain to enter scanning.
  useEffect(() => {
    if (screen !== "scanning") return;
    handleScanCompleted();
  }, [screen, loadingBalances, handleScanCompleted]);

  // const handleScanningComplete = useCallback(() => {
  //   if (screen === "scanning") {
  //     //  setScreen("no-asset");
  //     setScreen("scan-completed");
  //   } else if (screen === "waiting") {
  //     setScreen("transferring");
  //   } else if (screen === "transferring") {
  //     setScreen("transfer-completed");
  //   }
  // }, [screen]);

  const handleSelectAsset = useCallback((asset: Asset) => {
    setSelectedAsset(asset);
    setScreen("enter-amount");
  }, []);

  const handleConfirm = useCallback(async () => {
    try {
      if (!selectedAsset) {
        return;
      }
      setScreen("transferring");
      if (!userHasAllowance) {
        setTransferState("approving");
        const approvalTx = await approve(selectedAsset.chainId as any, +bridgeAmount, selectedAsset.symbol);
        console.log(approvalTx, "approvalTx");
      }
      setTransferState("transferring");
      const tx = await send(+bridgeAmount, selectedAsset.chainId as any, selectedAsset.symbol);

      setTransferState("block-confirming");
      setTransaction(tx);
      await confirmBlock(tx);
      setTransferState("claiming");
      const claimTx = await claimToken(tx);
      setClaimTx(claimTx);
      console.log(claimTx, "claimTx");
      setTransferState("approving");
      setScreen("transfer-completed");

      // setScreen("waiting");
    } catch (error) {
      console.error(error);
      setScreen("error");
    }
  }, [selectedAsset, bridgeAmount, userHasAllowance, approve, send, claimToken, confirmBlock]);

  const handleClose = useCallback(() => {
    setScreen("home");
    setSelectedAsset(null);
    setBridgeAmount("");
  }, []);

  const handleTransferAgain = useCallback(() => {
    setSelectedAsset(null);
    setBridgeAmount("");
    setScreen("home");
    getBalances([selectedScanChainId]);
  }, [getBalances, selectedScanChainId]);

  const handleRetry = useCallback(() => {
    handleScanWallet();
  }, [handleScanWallet]);

  const handleAmountChange = useCallback((amount: string) => {
    setBridgeAmount(amount);
  }, []);

  const handleRetryTransaction = useCallback(async () => {
    try {
      if (transferState == "approving") {
        handleConfirm();
        return;
      }
      if (!selectedAsset) {
        return;
      }
      setScreen("transferring");
      if (transferState == "transferring") {
        const tx = await send(+bridgeAmount, selectedAsset.chainId as any, selectedAsset.symbol);
        setTransferState("block-confirming");
        setTransaction(tx);
        await confirmBlock(tx);
        setTransferState("claiming");
        const claimTx = await claimToken(tx);
        setClaimTx(claimTx);
        console.log(claimTx, "claimTx");
        setTransferState("approving");
        setScreen("transfer-completed");
        return;
      }
      if (transferState == "block-confirming") {
        if (!transaction) {
          return;
        }
        await confirmBlock(transaction);
        setTransferState("claiming");
        const claimTx = await claimToken(transaction);
        setClaimTx(claimTx);
        console.log(claimTx, "claimTx");
        setTransferState("approving");
        setScreen("transfer-completed");
        return;
      }
      if (transferState == "claiming") {
        if (!transaction) {
          return;
        }
        const claimTx = await claimToken(transaction);
        setClaimTx(claimTx);
        console.log(claimTx, "claimTx");
        setTransferState("approving");
        setScreen("transfer-completed");
        return;
      }
    } catch (error) {
      console.error(error);
      setScreen("error");
    }
  }, [transaction, confirmBlock, selectedAsset, bridgeAmount, handleConfirm, send, claimToken, transferState]);

  const assets = useMemo(() => {
    if (!balances) {
      return [];
    }
    if (!decimals) {
      return [];
    }
    const chains = Object.keys(balances);
    const filteredChains = chains.filter((chainId) => {
      if (chainId === "sol.devnet" || chainId === "sol.mainnet") {
        return false;
      }
      if (chainId === "42421" || chainId === "42420") {
        return false;
      }
      return true;
    });
    const filteredBalances = filteredChains.flatMap((chainId) => {
      return balances[chainId as ChainId];
    });

    const assets = filteredBalances
      .filter((balance) => balance.amountCanBridge > 0 && supportedAssets.includes(balance.symbol))
      .map((balance) => {
        const decimal = decimals[balance.chainId as ChainId];
        if (!decimal) {
          return undefined;
        }
        const tokenDecimal = decimal.find((d) => d.symbol === balance.symbol);
        if (!tokenDecimal) {
          return undefined;
        }
        const tokenImage = getTokenImage(balance.symbol);
        const chainImage = getChainImage(balance.chainId);
        return {
          name: balance.symbol,
          symbol: balance.symbol,
          balance: balance.amountCanBridge.toString(),
          chainId: balance.chainId,
          tokenAddress: "",
          iconVariant: "usdc-alt",
          decimals: Number(tokenDecimal.decimal),
          tokenImage,
          chainImage,
        };
      });
    return assets.filter((asset) => asset !== undefined) as Asset[];
  }, [balances, decimals]);

  const data = useMemo(() => {
    if (!selectedAsset) {
      return null;
    }
    let symbol = selectedAsset.symbol;
    if (symbol === "XRWA") {
      symbol = "RWA";
    }
    const tokenBrideData = tokens.find((token) => token.symbol === symbol && token.chainId === selectedAsset.chainId);
    if (!tokenBrideData) {
      return null;
    }

    return tokenBrideData.bridgeInfos[0];
  }, [tokens, selectedAsset]);

  const handleBridge = useCallback(
    (amount: string) => {
      setBridgeAmount(amount);
      if (!data) {
        // show modal
        return;
      }
      const bridgeAmountInWei = getParsedBridgeAmountInWei(amount, BigInt(data.tokenDecimal));
      if (bridgeAmountInWei === undefined || bridgeAmountInWei <= 0n) {
        return;
      }

      const limitPerSendInWei = BigInt(data.limitPerSend);
      if (bridgeAmountInWei > limitPerSendInWei) {
        // show modal
        // can not transact more than the limit per send
        return;
      }

      setScreen("confirm");
    },
    [data]
  );

  const disableButton = useMemo(() => {
    if (!data) {
      return true;
    }
    const bridgeAmountInWei = getParsedBridgeAmountInWei(bridgeAmount, BigInt(data.tokenDecimal));
    if (bridgeAmountInWei === undefined || bridgeAmountInWei <= 0n) {
      return true;
    }
    const limitPerSendInWei = BigInt(data.limitPerSend);
    if (bridgeAmountInWei > limitPerSendInWei) {
      return true;
    }
    return false;
  }, [data, bridgeAmount]); // this is not working as expected it is not updating when the data or bridgeAmount changes

  const renderHomeScreen = useCallback(() => {
    return (
      <HomeScreen
        selectedScanChainId={selectedScanChainId}
        hasSelectedSourceChain={hasSelectedSourceChain}
        onSelectSourceChain={handleSelectSourceChain}
        onScanWallet={handleScanWallet}
        onClose={handleClose}
      />
    );
  }, [handleClose, handleScanWallet, handleSelectSourceChain, hasSelectedSourceChain, selectedScanChainId]);

  const renderAssetSelectionFallback = useCallback(() => {
    if (assets.length > 0) {
      return <ScanCompletedScreen assets={assets} onBack={() => setScreen("home")} onSelectAsset={handleSelectAsset} />;
    }

    return renderHomeScreen();
  }, [assets, handleSelectAsset, renderHomeScreen]);

  function renderScreen() {
    switch (screen) {
      case "home":
        return renderHomeScreen();

      case "scanning":
        return <ScanningScreen status="scanning" />;

      case "scan-completed":
        return (
          <ScanCompletedScreen assets={assets} onBack={() => setScreen("home")} onSelectAsset={handleSelectAsset} />
        );

      case "no-asset":
        return <NoAssetScreen onBack={() => setScreen("home")} onClose={handleClose} onRetry={handleRetry} />;

      case "enter-amount":
        if (!selectedAsset) {
          return renderAssetSelectionFallback();
        }

        return (
          <EnterAmountScreen
            asset={selectedAsset}
            bridgeInfo={data}
            onBack={() => setScreen("scan-completed")}
            onBridge={handleBridge}
            onAmountChange={handleAmountChange}
            disableButton={disableButton}
          />
        );

      case "confirm":
        if (!selectedAsset) {
          return renderAssetSelectionFallback();
        }

        return (
          <ConfirmScreen
            amount={bridgeAmount}
            assetName={selectedAsset.name}
            onBack={() => setScreen("enter-amount")}
            onClose={handleClose}
            onConfirm={handleConfirm}
          />
        );

      case "transferring":
        return <ScanningScreen status={transferState} />;

      case "transfer-completed":
        return (
          <TransferCompletedScreen
            amount={bridgeAmount}
            assetName={selectedAsset?.name || "USDT"}
            claimTx={claimTx || ""}
            toChain="42421"
            onClose={handleClose}
            onTransferAgain={handleTransferAgain}
          />
        );

      case "share-pnl":
        return <SharePNLScreen onClose={handleClose} />;

      case "error":
        return <ErrorScreen onRetry={handleRetryTransaction} onBack={handleClose} />;

      default:
        return renderHomeScreen();
    }
  }

  return <SEO title={getPageTitle(t`Bridge`)}>{renderScreen()}</SEO>;
}
